import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import {
  Activity, Users, MousePointerClick, Timer, BarChart3, Loader2, ChevronRight, Eye, Filter, Trophy, FlaskConical,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOWS = [
  { label: '24h', days: 1 },
  { label: '7d',  days: 7 },
  { label: '30d', days: 30 },
];

const fmtDuration = (s) => {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const EXPERIMENT_KEYS = [
  { key: 'demo-cta',         label: 'Hero CTA' },
  { key: 'pricing-headline', label: 'Pricing headline' },
];

const TelemetryPage = () => {
  const [days, setDays] = useState(7);
  const [demoOnly, setDemoOnly] = useState(true);
  const [experimentKey, setExperimentKey] = useState('demo-cta');
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [experiment, setExperiment] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Funnel + experiment endpoints are super-admin-only.  Fire summary
      // unconditionally; only request the others if we're likely super_admin.
      const sumRes = await axios.get(`${API}/admin/telemetry/summary?days=${days}&demo_only=${demoOnly}`, { withCredentials: true });
      setData(sumRes.data);
      const isGlobal = sumRes.data?.scope === 'global';
      if (isGlobal) {
        const [funRes, expRes] = await Promise.all([
          axios.get(`${API}/admin/telemetry/funnel?days=${days}`, { withCredentials: true }),
          axios.get(`${API}/admin/telemetry/funnel/by-variant?key=${experimentKey}&days=${days}`, { withCredentials: true }),
        ]);
        setFunnel(funRes.data);
        setExperiment(expRes.data);
      } else {
        setFunnel(null);
        setExperiment(null);
      }
    } catch {
      setData(null); setFunnel(null); setExperiment(null);
    } finally {
      setLoading(false);
    }
  }, [days, demoOnly, experimentKey]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const isTenant = data?.scope === 'tenant';
  const dailySeries = useMemo(() => {
    if (!data?.daily) return [];
    return data.daily.map((d) => ({
      day: d.day.slice(5), // MM-DD
      events: d.events,
      sessions: d.sessions,
    }));
  }, [data]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="telemetry-page">
        <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              {isTenant ? 'Usage Telemetry' : 'Demo Telemetry'}
            </h1>
            <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {isTenant
                ? 'How your team is using CivilX TMS. Scoped to your tenant.'
                : `What prospects actually click inside the sandbox. ${demoOnly ? 'Showing demo sessions only.' : 'Showing all users (demo + paid).'}`}
            </p>
          </div>
          <div className="flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                onClick={() => setDays(w.days)}
                data-testid={`telemetry-window-${w.label}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors ${days === w.days ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'}`}
              >
                {w.label}
              </button>
            ))}
            {!isTenant && (
              <label className="ml-2 inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={demoOnly}
                  onChange={(e) => setDemoOnly(e.target.checked)}
                  data-testid="telemetry-demo-only"
                  className="rounded border-slate-300"
                />
                Demo only
              </label>
            )}
          </div>
        </header>

        {loading ? (
          <div className="bg-white border border-slate-200 rounded-md p-10 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : !data ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center text-sm text-slate-500">
            No telemetry data yet for this window.
          </div>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="telemetry-kpis">
              <KpiCard icon={Users}             label="Sessions"     value={totals.total_sessions || 0} accent="bg-blue-50 text-[#002FA7]"   testid="kpi-sessions" />
              <KpiCard icon={Activity}          label="Events"       value={totals.total_events || 0}   accent="bg-amber-50 text-amber-700"  testid="kpi-events" />
              <KpiCard icon={Eye}               label="Users"        value={totals.total_users || 0}    accent="bg-emerald-50 text-emerald-700" testid="kpi-users" />
              <KpiCard icon={Timer}             label="Avg session"  value={fmtDuration(totals.avg_session_seconds)} accent="bg-purple-50 text-purple-700" testid="kpi-avg-session" />
            </div>

            {/* Daily area chart */}
            <Section title="Activity over time" icon={BarChart3} testid="telemetry-daily">
              {dailySeries.length === 0 ? (
                <Empty>No events recorded in this window.</Empty>
              ) : dailySeries.length === 1 ? (
                <div className="py-8 text-center" data-testid="telemetry-single-day-state">
                  <p className="text-sm text-slate-700 font-semibold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Only one day of data so far
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="font-bold text-slate-900 tabular-nums">{dailySeries[0].events}</span> events across <span className="font-bold text-slate-900 tabular-nums">{dailySeries[0].sessions}</span> session{dailySeries[0].sessions !== 1 ? 's' : ''} on {dailySeries[0].day}. Chart unlocks with day 2.
                  </p>
                </div>
              ) : (
                <div className="h-64 -ml-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dailySeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradEvents" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#002FA7" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#002FA7" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #E5E7EB' }}
                        labelStyle={{ fontWeight: 600, color: '#0F172A' }}
                      />
                      <Area type="monotone" dataKey="events"   stroke="#002FA7" strokeWidth={2} fill="url(#gradEvents)" name="Events" />
                      <Area type="monotone" dataKey="sessions" stroke="#F59E0B" strokeWidth={2} fill="transparent" name="Sessions" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Section>

            {!isTenant && (
              <>
                {/* Conversion funnel — always shown (across all users; demo-only filter
                    doesn't apply here because step 1 is by definition anonymous landing) */}
                <Section title="Demo conversion funnel" icon={Filter} testid="telemetry-funnel">
                  {!funnel || funnel.steps.every((s) => s.count === 0) ? (
                    <Empty>No funnel data yet — needs at least one anonymous landing visit.</Empty>
                  ) : (
                    <FunnelChart steps={funnel.steps} />
                  )}
                </Section>

                {/* CTA A/B Experiment */}
                <Section
                  title={<>CTA experiment — <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded ml-1">{experimentKey}</code></>}
                  icon={FlaskConical}
                  testid="telemetry-experiment"
                  action={
                    <select
                      value={experimentKey}
                      onChange={(e) => setExperimentKey(e.target.value)}
                      data-testid="telemetry-experiment-picker"
                      className="text-xs font-semibold border border-slate-300 rounded px-2 py-1 bg-white"
                    >
                      {EXPERIMENT_KEYS.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                    </select>
                  }
                >
                  {!experiment || !experiment.variants || experiment.variants.length === 0 ? (
                    <Empty>
                      No variant data yet for <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">{experimentKey}</code>. Visit <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">/</code> in a fresh tab to seed an impression — sessions are equal-split between variants A and B.
                    </Empty>
                  ) : (
                    <ExperimentBoard exp={experiment} />
                  )}
                </Section>
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top pages */}
              <Section title="Top pages" icon={Eye} testid="telemetry-pages">
                {data.top_pages.length === 0 ? (
                  <Empty>No page-views recorded.</Empty>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {data.top_pages.map((p, idx) => (
                      <li key={p.page} className="py-2.5 flex items-center justify-between text-sm" data-testid={`telemetry-page-row-${idx}`}>
                        <span className="font-mono text-slate-700 truncate pr-3">{p.page}</span>
                        <span className="font-semibold text-slate-900 tabular-nums">{p.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Top actions */}
              <Section title="Top actions" icon={MousePointerClick} testid="telemetry-actions">
                {data.top_actions.length === 0 ? (
                  <Empty>No clicks recorded yet — start dropping <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">track('click', …)</code> calls in components.</Empty>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {data.top_actions.map((a, idx) => (
                      <li key={`${a.target}-${idx}`} className="py-2.5 flex items-center justify-between text-sm" data-testid={`telemetry-action-row-${idx}`}>
                        <span className="flex items-center gap-2 min-w-0">
                          <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                          <span className="font-mono text-slate-700 truncate">{a.target}</span>
                          <span className="text-[10px] uppercase tracking-wider text-slate-400 flex-shrink-0">{a.event_type}</span>
                        </span>
                        <span className="font-semibold text-slate-900 tabular-nums flex-shrink-0">{a.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

const KpiCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testid} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
    <div className={`w-9 h-9 rounded-md flex items-center justify-center mb-3 ${accent}`}>
      <Icon className="w-4 h-4" strokeWidth={2.25} />
    </div>
    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
    <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums" style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</div>
  </div>
);

const Section = ({ title, icon: Icon, testid, children, action }) => (
  <section className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
        <Icon className="w-4 h-4 text-slate-500" strokeWidth={2.25} />
        {title}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

const Empty = ({ children }) => (
  <p className="text-sm text-slate-500 italic" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{children}</p>
);

const FunnelChart = ({ steps }) => {
  const topCount = steps[0]?.count || 1;
  const COLORS = ['#002FA7', '#1E5AC7', '#3B82F6', '#60A5FA'];
  return (
    <div className="space-y-2.5" data-testid="funnel-steps" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      {steps.map((s, idx) => {
        const widthPct = Math.max(8, Math.round((s.count / topCount) * 100));
        const dropFromPrev = idx > 0 ? steps[idx - 1].count - s.count : 0;
        return (
          <div key={s.key} data-testid={`funnel-step-${s.key}`}>
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-sm font-semibold text-slate-800">
                <span className="inline-block w-5 text-slate-400 tabular-nums">{idx + 1}.</span>
                {s.label}
              </span>
              <span className="text-xs text-slate-500 tabular-nums flex items-center gap-3">
                <span className="font-bold text-slate-900 text-base">{s.count}</span>
                {idx > 0 && (
                  <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${s.rate_from_prev >= 50 ? 'bg-emerald-50 text-emerald-700' : s.rate_from_prev >= 20 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                    {s.rate_from_prev}% from prev
                  </span>
                )}
                {idx > 0 && (
                  <span className="text-[11px] text-slate-400">{s.rate_from_top}% from top</span>
                )}
              </span>
            </div>
            <div className="relative h-7 bg-slate-100 rounded-md overflow-hidden">
              <div
                className="h-full rounded-md transition-all duration-500"
                style={{ width: `${widthPct}%`, backgroundColor: COLORS[idx] || '#002FA7' }}
              />
            </div>
            {idx > 0 && dropFromPrev > 0 && (
              <p className="text-[10px] text-slate-400 mt-1 pl-5">
                {dropFromPrev} session{dropFromPrev !== 1 ? 's' : ''} dropped off
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TelemetryPage;

const ExperimentBoard = ({ exp }) => {
  const variants = exp.variants || [];
  const winner = exp.winner;
  const sig = exp.significance;
  const sigPill = sig ? (() => {
    if (sig.verdict === 'highly_significant') return { text: `99% confidence (z=${sig.abs_z})`, cls: 'bg-emerald-50 border-emerald-300 text-emerald-800' };
    if (sig.verdict === 'significant')         return { text: `95% confidence (z=${sig.abs_z})`, cls: 'bg-emerald-50 border-emerald-300 text-emerald-800' };
    if (sig.verdict === 'trending')            return { text: `90% confidence (z=${sig.abs_z}) — trending`, cls: 'bg-amber-50 border-amber-300 text-amber-800' };
    return { text: `Not yet significant (z=${sig.abs_z})`, cls: 'bg-slate-50 border-slate-300 text-slate-600' };
  })() : null;
  return (
    <div style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="experiment-board">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {winner ? (
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-widest" data-testid="experiment-winner">
            <Trophy className="w-3.5 h-3.5" strokeWidth={2.5} />
            Winner — variant {winner}
          </div>
        ) : (
          <p className="text-[11px] text-slate-400 uppercase tracking-widest font-semibold">
            Collecting data — winner declared once each variant has ≥10 impressions and ≥5pp lift.
          </p>
        )}
        {sigPill && (
          <span
            data-testid="experiment-significance"
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-widest border ${sigPill.cls}`}
            title="Two-proportion z-test on conversion rate"
          >
            {sigPill.text}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {variants.map((v) => (
          <div
            key={v.variant}
            data-testid={`experiment-variant-${v.variant}`}
            className={`p-4 rounded-md border ${winner === v.variant ? 'border-emerald-400 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}
          >
            <div className="flex items-baseline justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${winner === v.variant ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                  {v.variant}
                </span>
                <span className="text-sm font-bold text-slate-900">
                  {v.demo_starts} / {v.impressions}
                </span>
              </div>
              <span className={`text-xl font-bold tabular-nums ${winner === v.variant ? 'text-emerald-700' : 'text-slate-900'}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {v.primary_conversion_pct}%
              </span>
            </div>
            <div className="space-y-1.5">
              {v.steps.map((s, idx) => {
                const top = v.steps[0].count || 1;
                const width = Math.max(6, Math.round((s.count / top) * 100));
                return (
                  <div key={s.key}>
                    <div className="flex items-center justify-between text-[11px] text-slate-600 mb-0.5">
                      <span>{idx + 1}. {s.label}</span>
                      <span className="tabular-nums">
                        <span className="font-bold text-slate-900">{s.count}</span>
                        {idx > 0 && <span className="text-slate-400 ml-2">{s.rate_from_prev}%</span>}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${winner === v.variant ? 'bg-emerald-500' : 'bg-[#002FA7]'}`} style={{ width: `${width}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
